import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, switchMap, withLatestFrom } from 'rxjs';

import { PaginationResponse } from '@shared/models/common.model';
import { ActiveDevicesService } from '../services/active-devices.service';
import * as ActiveDevicesActions from './active-devices.actions';
import { activeDevicesFeature } from './active-devices.reducer';

/**
 * Effects for the active-devices feature.
 *
 * The list itself is fetched via HTTP (paged) and reloaded after every
 * successful sign-out so the rich device metadata (OS, browser, last seen)
 * stays consistent. The realtime websocket store does not feed this list —
 * its only role for active-devices is to identify the current session so
 * the component can mark "this device" in the API rows.
 */
@Injectable()
export class ActiveDevicesEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly service = inject(ActiveDevicesService);

  readonly loadSessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActiveDevicesActions.loadSessions),
      withLatestFrom(
        this.store.select(activeDevicesFeature.selectPageIndex),
        this.store.select(activeDevicesFeature.selectPageSize)
      ),
      switchMap(([{ page, limit }, pageIndex, pageSize]) => {
        const resolvedPage = page ?? pageIndex + 1;
        const resolvedLimit = limit ?? pageSize;
        return this.service
          .listSessions({ page: resolvedPage, pageSize: resolvedLimit })
          .pipe(
            map(response => {
              const sessions = response.data ?? [];
              // Some gateways drop the pagination envelope on small pages.
              // Fill it in from what we asked for so the reducer always has
              // a complete `PaginationResponse` to write into state.
              const pagination: PaginationResponse = response.pagination ?? {
                page: resolvedPage,
                limit: resolvedLimit,
                total: sessions.length,
                totalPages: Math.max(
                  1,
                  Math.ceil(Math.max(sessions.length, 1) / resolvedLimit)
                ),
              };
              return ActiveDevicesActions.loadSessionsSuccess({
                sessions,
                pagination,
              });
            }),
            catchError(error =>
              of(
                ActiveDevicesActions.loadSessionsFailure({
                  error: normalizeError(error),
                })
              )
            )
          );
      })
    )
  );

  readonly deleteSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActiveDevicesActions.deleteSession),
      // `mergeMap` is intentional: a user may queue sign-outs across rows;
      // each request should fly through independently rather than
      // cancelling the previous one.
      mergeMap(({ sessionId }) =>
        this.service.deleteSession(sessionId).pipe(
          mergeMap(() => [
            ActiveDevicesActions.deleteSessionSuccess(),
            // Refetch the list so the row disappears with up-to-date
            // pagination — we can't rely on the websocket here because
            // active-devices is HTTP-driven by design.
            ActiveDevicesActions.loadSessions({}),
          ]),
          catchError(error =>
            of(
              ActiveDevicesActions.deleteSessionFailure({
                error: normalizeError(error),
              })
            )
          )
        )
      )
    )
  );

  readonly terminateOtherSessions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ActiveDevicesActions.terminateOtherSessions),
      // `switchMap` here: only one bulk-terminate makes sense in flight at
      // a time; rapid double-clicks should collapse to the latest.
      switchMap(() =>
        this.service.terminateOtherSessions().pipe(
          mergeMap(() => [
            ActiveDevicesActions.terminateOtherSessionsSuccess(),
            ActiveDevicesActions.loadSessions({}),
          ]),
          catchError(error =>
            of(
              ActiveDevicesActions.terminateOtherSessionsFailure({
                error: normalizeError(error),
              })
            )
          )
        )
      )
    )
  );
}

function normalizeError(err: unknown): string {
  const e = err as {
    error?: { message?: string | string[] | { message?: string } };
    message?: string;
  };

  if (Array.isArray(e?.error?.message)) {
    return e.error.message.length > 0
      ? String(e.error.message[0])
      : 'خطایی در ارتباط با سرور رخ داد. لطفاً دوباره تلاش کنید.';
  }

  if (
    e?.error?.message &&
    typeof e.error.message === 'object' &&
    'message' in e.error.message
  ) {
    return String((e.error.message as { message?: string }).message ?? '');
  }

  if (typeof e?.error?.message === 'string') {
    return e.error.message;
  }

  return (
    e?.message || 'خطایی در ارتباط با سرور رخ داد. لطفاً دوباره تلاش کنید.'
  );
}
